import { initLogger } from '../../../logger.js';
import { getErrorMessage } from '../../../error-utils.js';
import { JsonSchemaValidator } from '../json-schema-validator.js';
import { ValidationOutput } from '../validation.output.js';
import { applyArchitectureOptionsToPattern, convertJsonSchemaIssuesToValidationOutputs, findLatestCalmCoreSchemaUrl, prettifyJson, unansweredOneOfDecisions } from '../validation-helpers.js';
import { emptyRuleResult, ValidationPhase } from '../validation-rule.js';
/**
 * Structural (JSON-Schema / AJV) validation. Encapsulates the four historical mode-specific
 * behaviours verbatim so the observable output is unchanged:
 *  - architecture-with-pattern: compile the (option-resolved) pattern and validate the architecture;
 *    a compile failure aborts the remaining phases (controls/node-details).
 *  - architecture-only: discover the latest CALM core schema and validate against it; a compile
 *    failure records an error but does NOT abort subsequent phases.
 *  - pattern-only: compile the pattern to prove it is a valid schema (no instance validation).
 *  - timeline: compile the timeline schema and validate the timeline (compile errors propagate).
 */
export class JsonSchemaValidationRule {
    constructor() {
        this.id = 'json-schema';
        this.description = 'Validate the document against its JSON Schema (pattern / core / timeline)';
        this.phase = ValidationPhase.STRUCTURAL;
    }
    appliesTo() {
        return true;
    }
    async run(context) {
        const logger = initLogger(context.debug, 'calm-validate');
        switch (context.mode) {
            case 'architecture-with-pattern':
                return this.runArchitectureWithPattern(context, logger);
            case 'architecture-only':
                return this.runArchitectureOnly(context, logger);
            case 'pattern-only':
                return this.runPatternOnly(context);
            case 'timeline':
                return this.runTimeline(context, logger);
        }
    }
    async runArchitectureWithPattern(context, logger) {
        const architecture = context.architecture;
        const schemaDirectory = context.schemaDirectory;
        const patternResolved = applyArchitectureOptionsToPattern(architecture, context.pattern, context.debug);
        let jsonSchemaValidator;
        try {
            jsonSchemaValidator = new JsonSchemaValidator(schemaDirectory, patternResolved, context.debug);
            await jsonSchemaValidator.initialize();
        }
        catch (error) {
            const errorMessage = getErrorMessage(error);
            logger.error(`JSON Schema compilation failed: ${errorMessage}`);
            return {
                jsonSchemaOutputs: [ValidationOutput.error('json-schema', errorMessage, '/', { source: 'pattern' })],
                spectralOutputs: [],
                hasErrors: true,
                hasWarnings: false,
                abort: true
            };
        }
        const schemaErrors = jsonSchemaValidator.validate(architecture);
        if (schemaErrors.length > 0) {
            logger.debug(`JSON Schema validation raw output: ${prettifyJson(schemaErrors)}`);
        }
        const schemaOutputs = convertJsonSchemaIssuesToValidationOutputs(schemaErrors, 'architecture');
        // The pattern may already reject an empty answer, with `minItems` on its options list.
        const unanswered = unansweredOneOfDecisions(architecture, context.pattern)
            .filter(output => !schemaOutputs.some(existing => existing.path === output.path));
        const outputs = [...schemaOutputs, ...unanswered];
        if (outputs.length > 0) {
            return { ...emptyRuleResult(), jsonSchemaOutputs: outputs, hasErrors: true };
        }
        return emptyRuleResult();
    }
    async runArchitectureOnly(context, logger) {
        const architecture = context.architecture;
        const schemaDirectory = context.schemaDirectory;
        const coreSchemaUrl = schemaDirectory ? findLatestCalmCoreSchemaUrl(schemaDirectory) : undefined;
        const coreSchema = (schemaDirectory && coreSchemaUrl) ? await schemaDirectory.getSchema(coreSchemaUrl) : undefined;
        if (!schemaDirectory || !coreSchema) {
            logger.warn('No CALM core schema found in schema directory — skipping JSON schema validation');
            return emptyRuleResult();
        }
        logger.debug(`Validating architecture against CALM core schema: ${coreSchemaUrl}`);
        try {
            const jsonSchemaValidator = new JsonSchemaValidator(schemaDirectory, coreSchema, context.debug);
            await jsonSchemaValidator.initialize();
            const schemaErrors = jsonSchemaValidator.validate(architecture);
            if (schemaErrors.length > 0) {
                logger.debug(`JSON Schema validation raw output: ${prettifyJson(schemaErrors)}`);
                return {
                    ...emptyRuleResult(),
                    jsonSchemaOutputs: convertJsonSchemaIssuesToValidationOutputs(schemaErrors, 'architecture'),
                    hasErrors: true
                };
            }
            return emptyRuleResult();
        }
        catch (error) {
            const errorMessage = getErrorMessage(error);
            logger.error(`JSON Schema compilation failed: ${errorMessage}`);
            return {
                ...emptyRuleResult(),
                jsonSchemaOutputs: [ValidationOutput.error('json-schema', errorMessage, '/', { source: 'architecture' })],
                hasErrors: true
            };
        }
    }
    async runPatternOnly(context) {
        try {
            // Compile pattern as a schema to check if it's valid
            const jsonSchemaValidator = new JsonSchemaValidator(context.schemaDirectory, context.pattern, context.debug);
            await jsonSchemaValidator.initialize();
        }
        catch (error) {
            return {
                ...emptyRuleResult(),
                jsonSchemaOutputs: [ValidationOutput.error('json-schema', getErrorMessage(error), '/', { source: 'pattern' })],
                hasErrors: true
            };
        }
        return emptyRuleResult();
    }
    async runTimeline(context, logger) {
        // A compile failure here intentionally propagates (matches the historical timeline flow).
        const jsonSchemaValidator = new JsonSchemaValidator(context.schemaDirectory, context.pattern, context.debug);
        await jsonSchemaValidator.initialize();
        const schemaErrors = jsonSchemaValidator.validate(context.timeline);
        if (schemaErrors.length > 0) {
            logger.debug(`JSON Schema validation raw output: ${prettifyJson(schemaErrors)}`);
            return {
                ...emptyRuleResult(),
                jsonSchemaOutputs: convertJsonSchemaIssuesToValidationOutputs(schemaErrors, 'timeline'),
                hasErrors: true
            };
        }
        return emptyRuleResult();
    }
}
/** Convenience predicate for rules that only run when a SchemaDirectory + architecture exist. */
export function hasArchitectureAndDirectory(context) {
    return context.architecture !== undefined && context.schemaDirectory !== undefined;
}
//# sourceMappingURL=json-schema-rule.js.map